"use client";

import { useCallback } from "react";
import { AxiosError } from "axios";
import { useDatadog } from "@auth/client-sdk-react";
import {
  useSignupFlowStore,
} from "libs/state/src/lib/stores/flows/fresh/signupFlow/hooks/useSignUpFlowStore";
import { FormName } from "apps/website/types/form";

import { useAPI } from "./useAPI";
import { useSyncCustomerIdentity } from "./useSyncCustomerIdentity";

// Abandoned-cart failure reasons that mean the email now belongs to an existing
// customer. We surface these to the shopper and block checkout rather than taking
// payment against a conflicting account (mirrors the Cat Parent Details page).
export const EMAIL_CONFLICT_REASONS = new Set([
  "HAS_ALREADY_BEEN_CHARGED",
  "HAS_CONFLICTING_PERMISSIONS",
]);

/** Extracts the `reason` string from the abandoned-cart endpoint's error. */
export const extractAbandonedCartFailureReason = (err: unknown): string => {
  if (err instanceof Error && err.cause instanceof AxiosError) {
    return err.cause.response?.data?.reason ?? "UNKNOWN";
  }
  return "UNKNOWN";
};

export interface SyncAccountResult {
  blocked: boolean;
  userId?: string;
}

/**
 * Pushes the customer's latest store name/email to the `user` table at checkout.
 *
 * Reflects edits made in the checkout form, and from express checkout.
 * We pass `userId` so an email change updates their existing row rather than
 * creating a new one; the endpoint upserts by email and falls back to the id.
 *
 * A failure here never blocks payment, EXCEPT when the new email already
 * belongs to an existing customer, in which case we return `blocked` so the
 * caller can surface an error instead of charging against a conflicting account.
 *
 * Shared by both Express Checkout (`useCheckout`) and PayPal Checkout (`useCreateBasket` v2).
 */
export function useSyncAccountDetails(formUsed: FormName) {
  const { CustomerManagement: customerManagementAPI } = useAPI();
  const datadog = useDatadog();
  // Keeps analytics/observability (Segment, PostHog, Datadog) in sync when the
  // customer's name/email changes at checkout, mirroring the CP-details submit.
  const syncCustomerIdentity = useSyncCustomerIdentity();

  const syncAccountDetailsToUserTable = useCallback(async (
    currentUserId?: string,
  ): Promise<SyncAccountResult> => {
    const { user: latestUser, updateUser } = useSignupFlowStore.getState();
    const userId = currentUserId ?? latestUser.id;

    if (!latestUser.email) {
      return { blocked: false, userId };
    }

    if (!latestUser.firstName || !latestUser.lastName) {
      datadog.logger.warn(
        "[useSyncAccountDetails] syncing account with incomplete name",
        {
          eventName: "SyncAccountDetailsIncompleteName",
          hasFirstName: Boolean(latestUser.firstName),
          hasLastName: Boolean(latestUser.lastName),
          formUsed,
        },
      );
    }

    try {
      const userResponse = await customerManagementAPI.createOrUpdateAbandonedCartUser(
        {
          userId,
          firstName: latestUser.firstName,
          lastName: latestUser.lastName,
          email: latestUser.email,
          phoneNumber: "",
          persona: latestUser.persona ?? null,
        },
        { formUsed },
      );

      const { userId: newUserId, legacyCustomerId: oldUserId } = userResponse;
      syncCustomerIdentity({
        newUserId,
        oldUserId,
        email: latestUser.email,
        firstName: latestUser.firstName,
        lastName: latestUser.lastName,
      });

      // The user table is the source of truth for the id, so keep the store in sync.
      // Otherwise the store can hold an unusable id, and no later checkout attempt would work.
      if (newUserId && newUserId !== userId) {
        updateUser({ id: newUserId });
      }

      return { blocked: false, userId: newUserId || userId };
    } catch (syncError) {
      const reason = extractAbandonedCartFailureReason(syncError);
      if (EMAIL_CONFLICT_REASONS.has(reason)) {
        return { blocked: true };
      }
      datadog.logger.error(
        "[useSyncAccountDetails] failed to sync account details to user table",
        { eventName: "SyncAccountDetailsFailed", syncError, formUsed },
      );
      return { blocked: false, userId };
    }
  }, [ customerManagementAPI, syncCustomerIdentity, datadog, formUsed ]);

  return { syncAccountDetailsToUserTable };
}
